import { useCallback, useEffect, useState } from 'react';
import { api, type DashboardSummary, type SalesReport, type StockReport } from '../api';
import { HBars, VBars } from '../components/Charts';

function money(v: number, currency: string): string {
  try {
    return new Intl.NumberFormat('fr-CH', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${Math.round(v).toLocaleString('fr-CH')} ${currency}`;
  }
}

// "2026-07" -> "juil. 26"
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(y, m - 1, 1);
  return `${d.toLocaleDateString('fr-FR', { month: 'short' })} ${String(y).slice(2)}`;
}

export default function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [sales, setSales] = useState<SalesReport | null>(null);
  const [stock, setStock] = useState<StockReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Sequential: Odoo (staging) rate-limits parallel JSON-RPC bursts.
      setSummary(await api.getDashboardSummary());
      setSales(await api.getSalesReport());
      setStock(await api.getStockReport());
    } catch (e: any) {
      setError(e?.message ?? 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cur = summary?.currency ?? sales?.currency ?? 'CHF';

  return (
    <div className="dash">
      <div className="page-toolbar">
        <button className="btn-secondary" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Actualiser'}
        </button>
      </div>

      {error && <div className="dash-error">Erreur&nbsp;: {error}</div>}

      {/* KPI cards */}
      {summary && (
        <div className="kpi-grid">
          <KpiCard label="CA aujourd'hui" value={money(summary.revenue.today, cur)} />
          <KpiCard label="CA cette semaine" value={money(summary.revenue.week, cur)} />
          <KpiCard label="CA ce mois" value={money(summary.revenue.month, cur)} />
          <KpiCard label="CA cette année" value={money(summary.revenue.year, cur)} accent />
          <KpiCard label="Devis en attente" value={String(summary.pendingQuotes)} />
          <KpiCard
            label="Impayés clients"
            value={money(summary.receivables.total, cur)}
            hint={`${summary.receivables.count} facture(s)`}
            danger={summary.receivables.total > 0}
          />
          <KpiCard
            label="Produits sous seuil"
            value={String(summary.criticalProducts)}
            danger={summary.criticalProducts > 0}
          />
        </div>
      )}

      {/* Ventes */}
      <section className="dash-section">
        <h2>Ventes</h2>
        <div className="dash-cards">
          <div className="dash-card dash-card-wide">
            <div className="dash-card-title">Chiffre d'affaires — 12 derniers mois</div>
            {sales ? (
              <VBars items={sales.series.map((s) => ({ label: monthLabel(s.month), value: s.revenue }))} format={(v) => money(v, cur)} />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">Marge globale (commandes confirmées)</div>
            {sales ? (
              <div className="margin-box">
                <div className="margin-pct">{sales.margin.percent.toFixed(1)}%</div>
                <div className="margin-total">{money(sales.margin.total, cur)}</div>
              </div>
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">Top produits</div>
            {sales ? (
              <HBars
                items={sales.topProducts.map((p) => ({ label: p.name, value: p.revenue, sub: `· ${p.qty} u.` }))}
                format={(v) => money(v, cur)}
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">Top clients</div>
            {sales ? (
              <HBars
                items={sales.topCustomers.map((c) => ({ label: c.name, value: c.revenue }))}
                format={(v) => money(v, cur)}
                color="var(--color-accent)"
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
        </div>
      </section>

      {/* Stocks */}
      <section className="dash-section">
        <h2>Stocks</h2>
        <div className="dash-cards">
          <div className="dash-card">
            <div className="dash-card-title">
              Produits sous le minimum{stock ? ` (${stock.belowMin.length})` : ''}
            </div>
            {stock ? (
              <HBars
                items={stock.belowMin.slice(0, 12).map((p) => ({ label: p.name, value: p.onHand, sub: `/ min ${p.min}` }))}
                format={(v) => `${v}`}
                color="var(--color-danger)"
                emptyLabel="Aucun produit sous son seuil de réappro."
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">
              Ruptures (stock ≤ 0){stock ? ` — ${stock.outOfStockCount} au total` : ''}
            </div>
            {stock ? (
              <HBars
                items={stock.outOfStock.slice(0, 12).map((p) => ({ label: p.name, value: p.onHand, sub: `prév. ${p.forecast}` }))}
                format={(v) => `${v}`}
                color="var(--color-danger)"
                emptyLabel="Aucune rupture."
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
  danger,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
  danger?: boolean;
}) {
  return (
    <div className={`kpi-card${accent ? ' kpi-accent' : ''}${danger ? ' kpi-danger' : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}

function Placeholder({ loading }: { loading: boolean }) {
  return <div className="chart-empty">{loading ? 'Chargement…' : '—'}</div>;
}
